import { LinkOutlined } from "@ant-design/icons";
import { Popover, Tag } from "antd";
import { Link } from "react-router-dom";
import { sortAlphanumeric } from "utils/utils";
import { formatLinkedDesignPackage } from "../hooks/linked-design-packages";
import { TLinkedDesignPackageSummary } from "../model";

const VISIBLE_COUNT = 2;

function designPackagePath(projectId: string, id: string) {
  return `/project/${projectId}/design-packages/${id}/details`;
}

function LinkedDesignPackageTag(props: {
  designPackage: TLinkedDesignPackageSummary;
  projectId?: string;
}) {
  const { designPackage, projectId } = props;
  const label = formatLinkedDesignPackage(designPackage);

  const popupContent = (
    <div
      className="max-w-[239px] text-xs text-black text-opacity-80"
      data-testid="linked-design-package-tooltip"
    >
      {label}
    </div>
  );

  const tag = (
    <Popover trigger="hover" content={popupContent}>
      <Tag
        color="default"
        className="flex h-[54px] w-[160px] max-w-full m-0 space-x-0.5 items-start overflow-hidden bg-gray-100 py-[1.5px] px-1 print:h-auto"
        data-testid="linked-design-package-tag"
      >
        <span className="flex items-center shrink-0 p-1">
          <LinkOutlined className="fill-link-outlined" />
        </span>
        <span className="min-w-0 whitespace-normal break-words leading-tight line-clamp-3 print:line-clamp-none print:truncate">
          {label}
        </span>
      </Tag>
    </Popover>
  );

  if (!projectId || !designPackage.id) return tag;

  return (
    <Link
      to={designPackagePath(projectId, designPackage.id)}
      target="_blank"
      className="flex"
      data-testid="linked-design-package-link"
    >
      {tag}
    </Link>
  );
}

export default function LinkedDesignPackagesCell(props: {
  packages?: TLinkedDesignPackageSummary[] | null;
  projectId?: string;
}) {
  const packages = sortAlphanumeric(
    (props.packages ?? []).filter((item) =>
      Boolean(formatLinkedDesignPackage(item))
    ),
    "number"
  );

  if (!packages.length) return null;

  const toShow = packages.slice(0, VISIBLE_COUNT);
  const remaining = packages.slice(VISIBLE_COUNT);

  const popupContent = (
    <div className="max-w-[336px] m-0 text-xs flex flex-wrap gap-2">
      {remaining.map((item) => (
        <LinkedDesignPackageTag
          key={item.link_id ?? item.id}
          designPackage={item}
          projectId={props.projectId}
        />
      ))}
    </div>
  );

  return (
    <div
      className="h-full py-1 leading-none flex items-start gap-x-2"
      data-testid="linked-design-packages-cell"
    >
      {toShow.map((item) => (
        <div className="flex" key={item.link_id ?? item.id}>
          <LinkedDesignPackageTag
            designPackage={item}
            projectId={props.projectId}
          />
        </div>
      ))}
      {remaining.length ? (
        <div className="flex-none flex-grow-0 h-[54px] flex items-center">
          <Popover
            content={popupContent}
            title={<div className="py-1">Linked Design Package(s)</div>}
          >
            <div data-testid="linked-design-packages-more">
              +{remaining.length}
            </div>
          </Popover>
        </div>
      ) : null}
    </div>
  );
}
